import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import {
  User, MapPin, Phone, Mail, Edit2, Save, Camera,
  ShoppingBag, Home, Package, Loader2,
  Briefcase, DollarSign, FileText, CheckCircle, LayoutDashboard
} from 'lucide-react';
import { toast } from 'react-hot-toast';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, updateUser, loadUser, isLoading } = useAuthStore();
  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    address: '',
    city: '',
  });

  useEffect(() => {
    if (!user && !isLoading) {
      loadUser();
    }
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        username: user.username || '',
        email: user.email || '',
        address: user.address || '',
        city: user.city || '',
      });
    }
  }, [user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await updateUser(formData);
      setIsEditing(false);
      toast.success('پروفایل با موفقیت به‌روزرسانی شد');
    } catch (error) {
      toast.error('خطا در به‌روزرسانی پروفایل');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getRoleName = (role) => {
    switch (role) {
      case 'seller': return 'فروشنده';
      case 'installer': return 'نصاب';
      case 'admin': return 'ادمین';
      default: return 'مشتری';
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'seller': return 'bg-green-100 text-green-700';
      case 'installer': return 'bg-blue-100 text-blue-700';
      case 'admin': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // تب‌های مربوط به نقش کاربر
  const getTabs = () => {
    const baseTabs = [
      { id: 'profile', name: 'اطلاعات شخصی', icon: User, path: '/profile' },
    ];

    // برای مشتری
    if (user?.role === 'customer') {
      baseTabs.push(
        { id: 'orders', name: 'سفارشات من', icon: ShoppingBag, path: '/orders' },
        { id: 'installations', name: 'درخواست‌های نصب', icon: Home, path: '/installations' }
      );
    }

    // برای فروشنده - اضافه کردن داشبورد و سفارشات دریافتی
    if (user?.role === 'seller') {
      baseTabs.push(
        { id: 'dashboard', name: 'داشبورد فروشنده', icon: LayoutDashboard, path: '/seller/dashboard' },
        { id: 'products', name: 'مدیریت محصولات', icon: Package, path: '/seller/products' },
        { id: 'seller-orders', name: 'سفارشات دریافتی', icon: ShoppingBag, path: '/seller/orders' }
      );
    }

    // برای نصاب
    if (user?.role === 'installer') {
      baseTabs.push(
        { id: 'dashboard', name: 'داشبورد نصاب', icon: Briefcase, path: '/installer/dashboard' },
        { id: 'bids', name: 'پیشنهادات من', icon: FileText, path: '/installer/bids' },
        { id: 'contracts', name: 'قراردادها', icon: CheckCircle, path: '/installer/contracts' },
        { id: 'earnings', name: 'درآمدها', icon: DollarSign, path: '/installer/earnings' }
      );
    }

    return baseTabs;
  };

  const tabs = getTabs();
  const [activeTab, setActiveTab] = useState('profile');

  const handleTabClick = (tab) => {
    setActiveTab(tab.id);
    if (tab.path && tab.path !== '/profile') {
      navigate(tab.path);
    }
  };

  // در حال بارگذاری
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
        <span className="mr-3">در حال بارگذاری...</span>
      </div>
    );
  }

  // اگر کاربر وجود ندارد
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">لطفاً وارد شوید</h2>
          <Link to="/login" className="bg-blue-600 text-white px-6 py-2 rounded-lg">ورود</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* Sidebar */}
          <aside className="lg:w-1/4">
            <div className="bg-white rounded-xl shadow-sm p-6">
              <div className="text-center mb-6">
                <div className="relative inline-block">
                  <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden flex items-center justify-center">
                    {user?.avatar ? (
                      <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <User className="w-12 h-12 text-gray-400" />
                    )}
                  </div>
                  <button className="absolute bottom-0 left-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>
                <h2 className="font-bold text-lg">{user?.username || user?.phone}</h2>
                <p className="text-gray-500 text-sm">{user?.phone}</p>
                <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
                  {getRoleName(user?.role)}
                </span>
              </div>

              <nav className="space-y-2">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab)}
                    className={`w-full text-right px-4 py-3 rounded-lg flex items-center gap-3 transition-colors ${activeTab === tab.id
                        ? 'bg-blue-50 text-blue-700 font-medium'
                        : 'hover:bg-gray-50 text-gray-700'
                      }`}
                  >
                    {tab.icon && <tab.icon className="w-5 h-5" />}
                    <span>{tab.name}</span>
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="lg:w-3/4">
            {activeTab === 'profile' && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold">اطلاعات شخصی</h2>
                  <button
                    onClick={() => setIsEditing(!isEditing)}
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                  >
                    {isEditing ? (
                      <>
                        <Save className="w-4 h-4" />
                        ذخیره
                      </>
                    ) : (
                      <>
                        <Edit2 className="w-4 h-4" />
                        ویرایش
                      </>
                    )}
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        نام کاربری
                      </label>
                      <div className="relative">
                        <User className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="text"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                          disabled={!isEditing}
                          className="w-full border border-gray-300 rounded-lg pr-10 py-3 disabled:bg-gray-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        ایمیل
                      </label>
                      <div className="relative">
                        <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          disabled={!isEditing}
                          className="w-full border border-gray-300 rounded-lg pr-10 py-3 disabled:bg-gray-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        شماره موبایل
                      </label>
                      <div className="relative">
                        <Phone className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                          type="tel"
                          value={user?.phone || ''}
                          disabled
                          className="w-full border border-gray-300 rounded-lg pr-10 py-3 bg-gray-50"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        شهر
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        disabled={!isEditing}
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 disabled:bg-gray-50"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        آدرس
                      </label>
                      <div className="relative">
                        <MapPin className="absolute right-3 top-3 w-5 h-5 text-gray-400" />
                        <textarea
                          value={formData.address}
                          onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                          disabled={!isEditing}
                          rows={3}
                          className="w-full border border-gray-300 rounded-lg pr-10 py-3 disabled:bg-gray-50"
                        />
                      </div>
                    </div>
                  </div>

                  {isEditing && (
                    <div className="flex gap-3">
                      <button type="submit" disabled={isSubmitting} className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                        {isSubmitting ? 'در حال ذخیره...' : 'ذخیره تغییرات'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsEditing(false)}
                        className="border border-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-50"
                      >
                        انصراف
                      </button>
                    </div>
                  )}
                </form>
              </div>
            )}

            {/* تب‌های دیگر با لینک به صفحات جداگانه */}
            {activeTab !== 'profile' && (
              <div className="bg-white rounded-xl shadow-sm p-12 text-center">
                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">در حال انتقال به صفحه {tabs.find(t => t.id === activeTab)?.name}...</p>
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};